import { supabase } from './client';
import { Database } from '../../types/supabase';
import { AppState, Goal, Milestone, DailyCheckIn, FinancialEntry, FinancialTarget, ActionItem, ChatMessage, NetworkingContact, UserProfile, NotificationSettings, LifestylePreferences, LifeValues, LocationOption, PrivacyPreferences, CommunicationStyle, FinancialBaseline, GoalPriority, ActivityLogEntry, CoachingSession, CoachingMode, GoalTemplate, BudgetCategory, DebtAccount, LearningPath, LearningPathType, SavedJob, MockInterviewSession, Resume, ExportJob, ExportFormat, WebhookSubscription, AgentConfiguration, NotificationPreference, TrackingFrequency } from '../../types';
import { initialLifestylePreferences, initialLifeValues, initialPrivacyPreferences, initialCommunicationStyle } from '../../data/initialData';

// Result type for operations with user-facing error messages
export interface DataResult<T = void> {
  success: boolean;
  data?: T;
  error?: string; // User-facing error message
}

// Type aliases for database rows (using the auto-generated types directly)
type DbProfile = Database['public']['Tables']['profiles']['Row'];
type DbGoal = Database['public']['Tables']['goals']['Row'];
type DbMilestone = Database['public']['Tables']['milestones']['Row'];
type DbCheckIn = Database['public']['Tables']['daily_check_ins']['Row'];
type DbFinancialEntry = Database['public']['Tables']['financial_entries']['Row'];
type DbFinancialTarget = Database['public']['Tables']['financial_targets']['Row'];
type DbActionItem = Database['public']['Tables']['action_items']['Row'];
type DbChatMessage = Database['public']['Tables']['chat_messages']['Row'];
type DbContact = Database['public']['Tables']['networking_contacts']['Row'];
type DbNotificationSettings = Database['public']['Tables']['notification_settings']['Row'];
type DbLocationOption = Database['public']['Tables']['location_options']['Row'];

// Type helpers for safe casting
type GoalCategory = 'credential' | 'career' | 'financial' | 'networking' | 'personal';
type GoalStatus = 'not-started' | 'in-progress' | 'completed' | 'blocked';
type FinancialEntryType = 'income' | 'expense' | 'savings' | 'investment';
type ActionPriority = 'low' | 'medium' | 'high';
type ActionStatus = 'pending' | 'in-progress' | 'completed' | 'skipped';
type ActionCategory = 'daily' | 'weekly' | 'milestone';
type ChatRole = 'user' | 'coach';
type RelationshipStrength = 'cold' | 'warm' | 'strong';
type ContactCategory = 'recruiter' | 'peer' | 'mentor' | 'target-company' | 'other';
type CostOfLiving = 'low' | 'medium' | 'high' | 'very-high';
type FinancialTargetCategory = 'investment' | 'emergency-fund' | 'law-school' | 'relocation' | 'other';

// Map legacy path values to new CareerPath values
function mapLegacyPath(dbPath: string | null): import('../../types').CareerPath {
  // Legacy mappings
  if (dbPath === 'dubai-audit') return 'international';
  if (dbPath === 'miami-law') return 'career-pivot';
  // Return as-is if it's a valid new path, otherwise default to undecided
  const validPaths = [
    'climb-ladder', 'executive-track', 'management-track', 'technical-specialist',
    'entrepreneurship', 'freelance-consulting', 'portfolio-career',
    'remote-first', 'work-life-balance', 'fire',
    'career-pivot', 'international', 'academic-research', 'public-sector', 'creative-industries',
    'undecided'
  ];
  if (dbPath && validPaths.includes(dbPath)) {
    return dbPath as import('../../types').CareerPath;
  }
  return 'undecided';
}

// Convert snake_case DB fields to camelCase app fields
function parseChosenPaths(dbPaths: unknown, fallbackPath: string | null): import('../../types').CareerPath[] {
  if (Array.isArray(dbPaths) && dbPaths.length > 0) {
    return dbPaths.map((p: string) => mapLegacyPath(p));
  }
  // Backward compat: wrap single chosen_path in array
  const mapped = mapLegacyPath(fallbackPath);
  return mapped === 'undecided' ? [] : [mapped];
}

function dbProfileToApp(dbProfile: DbProfile): UserProfile {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p = dbProfile as any;
  const chosenPaths = parseChosenPaths(p.chosen_paths, dbProfile.chosen_path);
  return {
    name: dbProfile.name,
    currentRole: dbProfile.current_role || '',
    currentSalary: dbProfile.current_salary ?? 0,
    company: dbProfile.company || '',
    location: dbProfile.location || '',
    chosenPath: chosenPaths.length > 0 ? chosenPaths[0] : mapLegacyPath(dbProfile.chosen_path),
    chosenPaths,
    targetSalary: dbProfile.target_salary ?? 0,
    targetYear: dbProfile.target_year ?? new Date().getFullYear() + 5,
    startDate: dbProfile.start_date || new Date().toISOString().split('T')[0],
    phone: p.phone || undefined,
    timezone: p.timezone || undefined,
    photoUrl: p.photo_url || undefined,
    bio: p.bio || undefined,
    careerTarget: p.career_target || undefined,
    checkinFrequency: p.checkin_frequency || undefined,
  };
}

// Extract onboarding_complete from profile
function extractOnboardingComplete(dbProfile: DbProfile): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (dbProfile as any).onboarding_complete ?? false;
}

function dbGoalToApp(dbGoal: DbGoal, milestones: Milestone[]): Goal {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = dbGoal as any;
  return {
    id: dbGoal.id,
    title: dbGoal.title,
    description: dbGoal.description || '',
    category: (dbGoal.category as GoalCategory) || 'career',
    targetDate: dbGoal.target_date || '',
    status: (dbGoal.status as GoalStatus) || 'not-started',
    progress: dbGoal.progress ?? 0,
    milestones,
    priority: (g.priority as GoalPriority) || 'medium',
    archived: g.archived ?? false,
    archivedAt: g.archived_at || undefined,
    createdAt: dbGoal.created_at ?? new Date().toISOString(),
    updatedAt: dbGoal.updated_at ?? new Date().toISOString(),
  };
}

function dbMilestoneToApp(dbMilestone: DbMilestone): Milestone {
  return {
    id: dbMilestone.id,
    title: dbMilestone.title,
    completed: dbMilestone.completed ?? false,
    completedAt: dbMilestone.completed_at || undefined,
  };
}

function dbCheckInToApp(dbCheckIn: DbCheckIn): DailyCheckIn {
  return {
    id: dbCheckIn.id,
    date: dbCheckIn.date,
    mood: (dbCheckIn.mood as DailyCheckIn['mood']) ?? 3,
    energyLevel: (dbCheckIn.energy_level as DailyCheckIn['energyLevel']) ?? 3,
    tasksCompleted: dbCheckIn.tasks_completed ?? [],
    blockers: dbCheckIn.blockers ?? [],
    wins: dbCheckIn.wins ?? [],
    reflection: dbCheckIn.reflection || '',
    tomorrowPriorities: dbCheckIn.tomorrow_priorities ?? [],
  };
}

function dbFinancialEntryToApp(dbEntry: DbFinancialEntry): FinancialEntry {
  return {
    id: dbEntry.id,
    date: dbEntry.date,
    type: (dbEntry.type as FinancialEntryType) || 'expense',
    category: dbEntry.category || '',
    amount: dbEntry.amount ?? 0,
    description: dbEntry.description || '',
    recurring: dbEntry.recurring ?? false,
  };
}

function dbFinancialTargetToApp(dbTarget: DbFinancialTarget): FinancialTarget {
  return {
    id: dbTarget.id,
    name: dbTarget.name,
    targetAmount: dbTarget.target_amount ?? 0,
    currentAmount: dbTarget.current_amount ?? 0,
    deadline: dbTarget.deadline || '',
    category: (dbTarget.category as FinancialTargetCategory) || 'other',
  };
}

function dbActionItemToApp(dbItem: DbActionItem): ActionItem {
  return {
    id: dbItem.id,
    title: dbItem.title,
    description: dbItem.description || undefined,
    dueDate: dbItem.due_date || '',
    priority: (dbItem.priority as ActionPriority) || 'medium',
    status: (dbItem.status as ActionStatus) || 'pending',
    category: (dbItem.category as ActionCategory) || 'daily',
    dayNumber: dbItem.day_number ?? undefined,
    completedAt: dbItem.completed_at || undefined,
  };
}

type ChatContextType = 'check-in' | 'goal-update' | 'reflection' | 'advice' | 'celebration';

function dbChatMessageToApp(dbMsg: DbChatMessage): ChatMessage {
  return {
    id: dbMsg.id,
    role: (dbMsg.role as ChatRole) || 'user',
    content: dbMsg.content,
    timestamp: dbMsg.created_at ?? new Date().toISOString(),
    context: dbMsg.context_type ? {
      type: dbMsg.context_type as ChatContextType,
      relatedGoalId: dbMsg.context_related_goal_id || undefined,
    } : undefined,
  };
}

function dbNetworkingContactToApp(dbContact: DbContact): NetworkingContact {
  return {
    id: dbContact.id,
    name: dbContact.name,
    title: dbContact.title || '',
    company: dbContact.company || '',
    location: dbContact.location || '',
    linkedIn: dbContact.linkedin || undefined,
    email: dbContact.email || undefined,
    relationship: (dbContact.relationship as RelationshipStrength) || 'cold',
    lastContact: dbContact.last_contact || undefined,
    notes: dbContact.notes || '',
    category: (dbContact.category as ContactCategory) || 'other',
  };
}

function dbNotificationSettingsToApp(dbSettings: DbNotificationSettings): NotificationSettings {
  return {
    enabled: dbSettings.enabled ?? false,
    dailyReminderTime: dbSettings.daily_reminder_time || '09:00',
    reminderTypes: {
      checkIn: dbSettings.check_in_reminders ?? true,
      tasks: dbSettings.task_reminders ?? true,
      goals: dbSettings.goal_reminders ?? true,
      motivational: dbSettings.motivational_reminders ?? true,
    },
  };
}

function dbLocationOptionToApp(dbLocation: DbLocationOption): LocationOption {
  return {
    id: dbLocation.id,
    city: dbLocation.city,
    state: dbLocation.state || undefined,
    country: dbLocation.country,
    notes: dbLocation.notes || '',
    costOfLiving: (dbLocation.cost_of_living as CostOfLiving) || undefined,
  };
}

// Extract lifestyle preferences from profile JSONB
function extractLifestylePreferences(jsonb: unknown): LifestylePreferences {
  if (!jsonb || typeof jsonb !== 'object') {
    return initialLifestylePreferences;
  }
  const data = jsonb as Record<string, unknown>;
  return {
    hobbies: Array.isArray(data.hobbies) ? data.hobbies : [],
    activityLevel: (data.activityLevel as LifestylePreferences['activityLevel']) || 'moderate',
    outdoorPreference: (data.outdoorPreference as LifestylePreferences['outdoorPreference']) || 3,
    climatePreference: (data.climatePreference as LifestylePreferences['climatePreference']) || 'temperate',
    seasonImportance: Array.isArray(data.seasonImportance) ? data.seasonImportance : [],
    workStyle: (data.workStyle as LifestylePreferences['workStyle']) || 'hybrid',
    travelTolerance: (data.travelTolerance as LifestylePreferences['travelTolerance']) || 'occasional',
    familyProximityImportance: (data.familyProximityImportance as LifestylePreferences['familyProximityImportance']) || 3,
    familyLocations: Array.isArray(data.familyLocations) ? data.familyLocations : [],
    socialStyle: (data.socialStyle as LifestylePreferences['socialStyle']) || 'ambivert',
  };
}

// Extract life values from profile JSONB
function extractLifeValues(jsonb: unknown): LifeValues {
  if (!jsonb || typeof jsonb !== 'object') {
    return initialLifeValues;
  }
  const data = jsonb as Record<string, unknown>;
  return {
    careerGrowth: (data.careerGrowth as LifeValues['careerGrowth']) || 3,
    workLifeBalance: (data.workLifeBalance as LifeValues['workLifeBalance']) || 3,
    adventure: (data.adventure as LifeValues['adventure']) || 3,
    stability: (data.stability as LifeValues['stability']) || 3,
    familyTime: (data.familyTime as LifeValues['familyTime']) || 3,
    financialGrowth: (data.financialGrowth as LifeValues['financialGrowth']) || 3,
    health: (data.health as LifeValues['health']) || 3,
    community: (data.community as LifeValues['community']) || 3,
  };
}

// User-friendly error message generator
function getUserFriendlyError(operation: string, error: unknown): string {
  const errorMessage = error instanceof Error ? error.message : String(error);

  // Check for common error patterns
  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }
  if (errorMessage.includes('timeout')) {
    return 'The request took too long. Please try again.';
  }
  if (errorMessage.includes('PGRST301') || errorMessage.includes('JWT')) {
    return 'Your session has expired. Please sign in again.';
  }
  if (errorMessage.includes('23505')) {
    return 'This item already exists.';
  }
  if (errorMessage.includes('23503')) {
    return 'Cannot complete this action - related data is missing.';
  }

  // Generic fallback
  return `Unable to ${operation}. Please try again later.`;
}

export async function loadUserData(userId: string): Promise<DataResult<Partial<AppState>>> {
  try {
    // Load all data in parallel
    const [
      profileRes,
      goalsRes,
      milestonesRes,
      checkInsRes,
      financialEntriesRes,
      financialTargetsRes,
      actionItemsRes,
      chatMessagesRes,
      contactsRes,
      notificationSettingsRes,
      locationOptionsRes,
    ] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).single(),
      supabase.from('goals').select('*').eq('user_id', userId),
      supabase.from('milestones').select('*').eq('user_id', userId),
      supabase.from('daily_check_ins').select('*').eq('user_id', userId).order('date', { ascending: false }),
      supabase.from('financial_entries').select('*').eq('user_id', userId).order('date', { ascending: false }),
      supabase.from('financial_targets').select('*').eq('user_id', userId),
      supabase.from('action_items').select('*').eq('user_id', userId).order('due_date', { ascending: true }),
      supabase.from('chat_messages').select('*').eq('user_id', userId).order('created_at', { ascending: true }),
      supabase.from('networking_contacts').select('*').eq('user_id', userId),
      supabase.from('notification_settings').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('location_options').select('*').eq('user_id', userId).order('created_at', { ascending: true }),
    ]);

    // Check for critical errors (profile load failure other than "not found")
    if (profileRes.error && profileRes.error.code !== 'PGRST116') {
      console.error('Error loading profile:', profileRes.error);
      return { success: false, error: getUserFriendlyError('load your profile', profileRes.error) };
    }

    // If no profile exists, user hasn't been set up yet
    if (!profileRes.data) {
      return { success: true, data: undefined };
    }

    // Group milestones by goal_id
    const milestonesByGoal: Record<string, Milestone[]> = {};
    (milestonesRes.data || []).forEach((m) => {
      const goalId = m.goal_id;
      if (!milestonesByGoal[goalId]) {
        milestonesByGoal[goalId] = [];
      }
      milestonesByGoal[goalId].push(dbMilestoneToApp(m));
    });

    // Convert goals with their milestones
    const goals = (goalsRes.data || []).map((g) =>
      dbGoalToApp(g, milestonesByGoal[g.id] || [])
    );

    // Extract life planning data from profile JSONB columns
    const profileData = profileRes.data;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pd = profileData as any;

    return {
      success: true,
      data: {
        profile: dbProfileToApp(profileData),
        goals,
        dailyCheckIns: (checkInsRes.data || []).map(dbCheckInToApp),
        weeklyReviews: [],
        financialEntries: (financialEntriesRes.data || []).map(dbFinancialEntryToApp),
        financialTargets: (financialTargetsRes.data || []).map(dbFinancialTargetToApp),
        actionItems: (actionItemsRes.data || []).map(dbActionItemToApp),
        chatHistory: (chatMessagesRes.data || []).map(dbChatMessageToApp),
        networkingContacts: (contactsRes.data || []).map(dbNetworkingContactToApp),
        notificationSettings: notificationSettingsRes.data
          ? dbNotificationSettingsToApp(notificationSettingsRes.data)
          : { enabled: false, dailyReminderTime: '09:00', reminderTypes: { checkIn: true, tasks: true, goals: true, motivational: true } },
        userPlaybooks: [],
        // Life Planning data
        lifestylePreferences: extractLifestylePreferences(profileData.lifestyle_preferences),
        lifeValues: extractLifeValues(profileData.life_values),
        locationOptions: (locationOptionsRes.data || []).map(dbLocationOptionToApp),
        // Onboarding status
        onboardingComplete: extractOnboardingComplete(profileData),
        // Phase 1 additions
        privacyPreferences: (pd.privacy_preferences as PrivacyPreferences) || initialPrivacyPreferences,
        communicationStyle: (pd.communication_style as CommunicationStyle) || initialCommunicationStyle,
        financialBaseline: (pd.financial_baseline as FinancialBaseline) || undefined,
        agentPaused: pd.agent_paused ?? false,
      }
    };
  } catch (error) {
    console.error('Error loading user data:', error);
    return { success: false, error: getUserFriendlyError('load your data', error) };
  }
}

export async function saveProfile(userId: string, profile: UserProfile): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').upsert({
      id: userId,
      name: profile.name,
      current_role: profile.currentRole,
      current_salary: profile.currentSalary,
      company: profile.company,
      location: profile.location,
      chosen_path: profile.chosenPaths.length > 0 ? profile.chosenPaths[0] : profile.chosenPath,
      chosen_paths: profile.chosenPaths,
      target_salary: profile.targetSalary,
      target_year: profile.targetYear,
      start_date: profile.startDate,
      phone: profile.phone || null,
      timezone: profile.timezone || null,
      photo_url: profile.photoUrl || null,
      bio: profile.bio || null,
      career_target: profile.careerTarget || null,
      checkin_frequency: profile.checkinFrequency || null,
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.error('Error saving profile:', error);
      return { success: false, error: getUserFriendlyError('save your profile', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving profile:', error);
    return { success: false, error: getUserFriendlyError('save your profile', error) };
  }
}

export async function saveGoal(userId: string, goal: Goal): Promise<DataResult> {
  try {
    // Save goal
    const { error: goalError } = await supabase.from('goals').upsert({
      id: goal.id,
      user_id: userId,
      title: goal.title,
      description: goal.description,
      category: goal.category,
      target_date: goal.targetDate,
      status: goal.status,
      progress: goal.progress,
      priority: goal.priority || 'medium',
      archived: goal.archived || false,
      archived_at: goal.archivedAt || null,
      updated_at: new Date().toISOString(),
    });

    if (goalError) {
      console.error('Error saving goal:', goalError);
      return { success: false, error: getUserFriendlyError('save your goal', goalError) };
    }

    // Save milestones
    if (goal.milestones.length > 0) {
      const milestones = goal.milestones.map((m, index) => ({
        id: m.id,
        goal_id: goal.id,
        user_id: userId,
        title: m.title,
        completed: m.completed,
        completed_at: m.completedAt || null,
        sort_order: index,
      }));

      const { error: milestonesError } = await supabase.from('milestones').upsert(milestones);

      if (milestonesError) {
        console.error('Error saving milestones:', milestonesError);
        return { success: false, error: getUserFriendlyError('save goal milestones', milestonesError) };
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving goal:', error);
    return { success: false, error: getUserFriendlyError('save your goal', error) };
  }
}

export async function deleteGoal(userId: string, goalId: string): Promise<DataResult> {
  try {
    // Delete milestones first (foreign key constraint)
    await supabase.from('milestones').delete().eq('goal_id', goalId).eq('user_id', userId);

    // Delete goal
    const { error } = await supabase.from('goals').delete().eq('id', goalId).eq('user_id', userId);

    if (error) {
      console.error('Error deleting goal:', error);
      return { success: false, error: getUserFriendlyError('delete this goal', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error deleting goal:', error);
    return { success: false, error: getUserFriendlyError('delete this goal', error) };
  }
}

export async function saveCheckIn(userId: string, checkIn: DailyCheckIn): Promise<DataResult> {
  try {
    const { error } = await supabase.from('daily_check_ins').upsert({
      id: checkIn.id,
      user_id: userId,
      date: checkIn.date,
      mood: checkIn.mood,
      energy_level: checkIn.energyLevel,
      tasks_completed: checkIn.tasksCompleted,
      blockers: checkIn.blockers,
      wins: checkIn.wins,
      reflection: checkIn.reflection,
      tomorrow_priorities: checkIn.tomorrowPriorities,
    });

    if (error) {
      console.error('Error saving check-in:', error);
      return { success: false, error: getUserFriendlyError('save your check-in', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving check-in:', error);
    return { success: false, error: getUserFriendlyError('save your check-in', error) };
  }
}

export async function saveActionItem(userId: string, item: ActionItem): Promise<DataResult> {
  try {
    const { error } = await supabase.from('action_items').upsert({
      id: item.id,
      user_id: userId,
      title: item.title,
      description: item.description || null,
      due_date: item.dueDate || null,
      priority: item.priority,
      status: item.status,
      category: item.category,
      day_number: item.dayNumber || null,
      completed_at: item.completedAt || null,
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.error('Error saving action item:', error);
      return { success: false, error: getUserFriendlyError('save this task', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving action item:', error);
    return { success: false, error: getUserFriendlyError('save this task', error) };
  }
}

export async function saveFinancialTarget(userId: string, target: FinancialTarget): Promise<DataResult> {
  try {
    const { error } = await supabase.from('financial_targets').upsert({
      id: target.id,
      user_id: userId,
      name: target.name,
      target_amount: target.targetAmount,
      current_amount: target.currentAmount,
      deadline: target.deadline || null,
      category: target.category,
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.error('Error saving financial target:', error);
      return { success: false, error: getUserFriendlyError('save your financial target', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving financial target:', error);
    return { success: false, error: getUserFriendlyError('save your financial target', error) };
  }
}

export async function saveChatMessage(userId: string, message: ChatMessage): Promise<DataResult> {
  try {
    const { error } = await supabase.from('chat_messages').insert({
      id: message.id,
      user_id: userId,
      role: message.role,
      content: message.content,
      context_type: message.context?.type || null,
      context_related_goal_id: message.context?.relatedGoalId || null,
      created_at: message.timestamp,
    });

    if (error) {
      console.error('Error saving chat message:', error);
      return { success: false, error: getUserFriendlyError('save your message', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving chat message:', error);
    return { success: false, error: getUserFriendlyError('save your message', error) };
  }
}

export async function saveNotificationSettings(userId: string, settings: NotificationSettings): Promise<DataResult> {
  try {
    const { error } = await supabase.from('notification_settings').upsert({
      user_id: userId,
      enabled: settings.enabled,
      daily_reminder_time: settings.dailyReminderTime,
      check_in_reminders: settings.reminderTypes.checkIn,
      task_reminders: settings.reminderTypes.tasks,
      goal_reminders: settings.reminderTypes.goals,
      motivational_reminders: settings.reminderTypes.motivational,
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.error('Error saving notification settings:', error);
      return { success: false, error: getUserFriendlyError('save notification settings', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving notification settings:', error);
    return { success: false, error: getUserFriendlyError('save notification settings', error) };
  }
}

// Initialize a new user's profile
export async function initializeUserProfile(userId: string, name: string): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').insert({
      id: userId,
      name: name,
      current_salary: 0,
      target_salary: 0,
      start_date: new Date().toISOString().split('T')[0],
    });

    if (error && error.code !== '23505') { // Ignore duplicate key errors
      console.error('Error initializing profile:', error);
      return { success: false, error: getUserFriendlyError('create your profile', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error initializing profile:', error);
    return { success: false, error: getUserFriendlyError('create your profile', error) };
  }
}

// Save lifestyle preferences (stored in profile JSONB column)
export async function saveLifestylePreferences(userId: string, preferences: LifestylePreferences): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      lifestyle_preferences: JSON.parse(JSON.stringify(preferences)),
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving lifestyle preferences:', error);
      return { success: false, error: getUserFriendlyError('save your lifestyle preferences', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving lifestyle preferences:', error);
    return { success: false, error: getUserFriendlyError('save your lifestyle preferences', error) };
  }
}

// Save life values (stored in profile JSONB column)
export async function saveLifeValues(userId: string, values: LifeValues): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      life_values: JSON.parse(JSON.stringify(values)),
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving life values:', error);
      return { success: false, error: getUserFriendlyError('save your life values', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving life values:', error);
    return { success: false, error: getUserFriendlyError('save your life values', error) };
  }
}

// Save location option
export async function saveLocationOption(userId: string, location: LocationOption): Promise<DataResult> {
  try {
    const { error } = await supabase.from('location_options').upsert({
      id: location.id,
      user_id: userId,
      city: location.city,
      state: location.state || null,
      country: location.country,
      notes: location.notes || '',
      cost_of_living: location.costOfLiving || null,
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.error('Error saving location option:', error);
      return { success: false, error: getUserFriendlyError('save your location', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving location option:', error);
    return { success: false, error: getUserFriendlyError('save your location', error) };
  }
}

// Delete location option
export async function deleteLocationOption(userId: string, locationId: string): Promise<DataResult> {
  try {
    const { error } = await supabase.from('location_options')
      .delete()
      .eq('id', locationId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting location option:', error);
      return { success: false, error: getUserFriendlyError('delete this location', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error deleting location option:', error);
    return { success: false, error: getUserFriendlyError('delete this location', error) };
  }
}

// Save onboarding completion status
export async function saveOnboardingStatus(userId: string, complete: boolean): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      onboarding_complete: complete,
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving onboarding status:', error);
      return { success: false, error: getUserFriendlyError('save onboarding status', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving onboarding status:', error);
    return { success: false, error: getUserFriendlyError('save onboarding status', error) };
  }
}

// Save onboarding step progress (called after each step)
export async function saveOnboardingStep(userId: string, step: number): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      onboarding_step: step,
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving onboarding step:', error);
      return { success: false, error: getUserFriendlyError('save onboarding step', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving onboarding step:', error);
    return { success: false, error: getUserFriendlyError('save onboarding step', error) };
  }
}

// Save onboarding progress with profile/lifestyle/values data
export async function saveOnboardingProgress(
  userId: string,
  step: number,
  data: {
    profile?: Record<string, unknown>;
    lifestyle?: Record<string, unknown>;
    values?: Record<string, unknown>;
    financialBaseline?: Record<string, unknown>;
  }
): Promise<DataResult> {
  try {
    const updateData: Record<string, unknown> = {
      onboarding_step: step,
      updated_at: new Date().toISOString(),
    };

    // Only include fields that have data
    if (data.profile) {
      // Map profile fields to DB columns
      if (data.profile.name !== undefined) updateData.name = data.profile.name;
      if (data.profile.currentRole !== undefined) updateData.current_role = data.profile.currentRole;
      if (data.profile.company !== undefined) updateData.company = data.profile.company;
      if (data.profile.location !== undefined) updateData.location = data.profile.location;
      if (data.profile.currentSalary !== undefined) updateData.current_salary = data.profile.currentSalary;
      if (data.profile.targetSalary !== undefined) updateData.target_salary = data.profile.targetSalary;
      if (data.profile.targetYear !== undefined) updateData.target_year = data.profile.targetYear;
      if (data.profile.chosenPath !== undefined) updateData.chosen_path = data.profile.chosenPath;
      if (data.profile.chosenPaths !== undefined) updateData.chosen_paths = data.profile.chosenPaths;
      if (data.profile.careerTarget !== undefined) updateData.career_target = data.profile.careerTarget;
      if (data.profile.checkinFrequency !== undefined) updateData.checkin_frequency = data.profile.checkinFrequency;
    }

    if (data.financialBaseline) {
      updateData.financial_baseline = data.financialBaseline;
    }

    if (data.lifestyle) {
      updateData.lifestyle_preferences = data.lifestyle;
    }

    if (data.values) {
      updateData.life_values = data.values;
    }

    const { error } = await supabase.from('profiles').update(updateData).eq('id', userId);

    if (error) {
      console.error('Error saving onboarding progress:', error);
      return { success: false, error: getUserFriendlyError('save onboarding progress', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving onboarding progress:', error);
    return { success: false, error: getUserFriendlyError('save onboarding progress', error) };
  }
}

// Archive a goal (soft delete)
export async function archiveGoal(userId: string, goalId: string): Promise<DataResult> {
  try {
    const { error } = await supabase.from('goals').update({
      archived: true,
      archived_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).eq('id', goalId).eq('user_id', userId);

    if (error) {
      console.error('Error archiving goal:', error);
      return { success: false, error: getUserFriendlyError('archive this goal', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error archiving goal:', error);
    return { success: false, error: getUserFriendlyError('archive this goal', error) };
  }
}

// Unarchive a goal
export async function unarchiveGoal(userId: string, goalId: string): Promise<DataResult> {
  try {
    const { error } = await supabase.from('goals').update({
      archived: false,
      archived_at: null,
      updated_at: new Date().toISOString(),
    }).eq('id', goalId).eq('user_id', userId);

    if (error) {
      console.error('Error unarchiving goal:', error);
      return { success: false, error: getUserFriendlyError('unarchive this goal', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error unarchiving goal:', error);
    return { success: false, error: getUserFriendlyError('unarchive this goal', error) };
  }
}

// Save privacy preferences
export async function savePrivacyPreferences(userId: string, prefs: PrivacyPreferences): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      privacy_preferences: JSON.parse(JSON.stringify(prefs)),
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving privacy preferences:', error);
      return { success: false, error: getUserFriendlyError('save privacy settings', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving privacy preferences:', error);
    return { success: false, error: getUserFriendlyError('save privacy settings', error) };
  }
}

// Save communication style
export async function saveCommunicationStyle(userId: string, style: CommunicationStyle): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      communication_style: JSON.parse(JSON.stringify(style)),
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving communication style:', error);
      return { success: false, error: getUserFriendlyError('save communication style', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving communication style:', error);
    return { success: false, error: getUserFriendlyError('save communication style', error) };
  }
}

// Save financial baseline
export async function saveFinancialBaseline(userId: string, baseline: FinancialBaseline): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      financial_baseline: JSON.parse(JSON.stringify(baseline)),
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving financial baseline:', error);
      return { success: false, error: getUserFriendlyError('save financial baseline', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving financial baseline:', error);
    return { success: false, error: getUserFriendlyError('save financial baseline', error) };
  }
}

// Save agent paused state
export async function saveAgentPaused(userId: string, paused: boolean): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      agent_paused: paused,
      agent_paused_at: paused ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    }).eq('id', userId);

    if (error) {
      console.error('Error saving agent pause state:', error);
      return { success: false, error: getUserFriendlyError('update agent status', error) };
    }
    return { success: true };
  } catch (error) {
    console.error('Error saving agent pause state:', error);
    return { success: false, error: getUserFriendlyError('update agent status', error) };
  }
}

// Get onboarding progress to resume where user left off
export async function getOnboardingProgress(userId: string): Promise<{
  step: number;
  profile: Record<string, unknown>;
  lifestyle: Record<string, unknown>;
  values: Record<string, unknown>;
} | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('onboarding_step, name, current_role, company, location, current_salary, target_salary, target_year, chosen_path, chosen_paths, lifestyle_preferences, life_values, onboarding_complete')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error getting onboarding progress:', error);
      return null;
    }

    if (!data) return null;

    // If onboarding is complete, return null (no need to resume)
    if (data.onboarding_complete) return null;

    return {
      step: data.onboarding_step || 0,
      profile: {
        name: data.name || '',
        currentRole: data.current_role || '',
        company: data.company || '',
        location: data.location || '',
        currentSalary: data.current_salary || 0,
        targetSalary: data.target_salary || 0,
        targetYear: data.target_year || new Date().getFullYear() + 3,
        chosenPath: data.chosen_path || 'undecided',
        chosenPaths: Array.isArray((data as Record<string, unknown>).chosen_paths) ? (data as Record<string, unknown>).chosen_paths : [],
      },
      lifestyle: (data.lifestyle_preferences as Record<string, unknown>) || {},
      values: (data.life_values as Record<string, unknown>) || {},
    };
  } catch (error) {
    console.error('Error getting onboarding progress:', error);
    return null;
  }
}

// ============================================
// PHASE 2-14 DATA SERVICE FUNCTIONS
// ============================================

// Activity Log
export async function loadActivityLog(userId: string, limit = 50): Promise<DataResult<ActivityLogEntry[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase
      .from('user_activity_log' as any)
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load activity', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id,
        activityType: d.activity_type,
        entityType: d.entity_type,
        entityId: d.entity_id,
        title: d.title,
        description: d.description,
        metadata: d.metadata,
        createdAt: d.created_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load activity', error) };
  }
}

export async function logActivity(userId: string, entry: Omit<ActivityLogEntry, 'id' | 'createdAt'>): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('user_activity_log' as any).insert({
      user_id: userId,
      activity_type: entry.activityType,
      entity_type: entry.entityType || null,
      entity_id: entry.entityId || null,
      title: entry.title,
      description: entry.description || null,
      metadata: entry.metadata || {},
    });
    if (error) return { success: false, error: getUserFriendlyError('log activity', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('log activity', error) };
  }
}

// Coaching Sessions
export async function loadCoachingSessions(userId: string): Promise<DataResult<CoachingSession[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase
      .from('coaching_sessions' as any)
      .select('*')
      .eq('user_id', userId)
      .order('last_activity_at', { ascending: false }) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load sessions', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id,
        mode: d.mode as CoachingMode,
        title: d.title,
        status: d.status,
        context: d.context,
        startedAt: d.started_at,
        lastActivityAt: d.last_activity_at,
        messageCount: d.message_count,
        createdAt: d.created_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load sessions', error) };
  }
}

export async function saveCoachingSession(userId: string, session: Partial<CoachingSession> & { id: string }): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('coaching_sessions' as any).upsert({
      id: session.id,
      user_id: userId,
      mode: session.mode || 'general',
      title: session.title || null,
      status: session.status || 'active',
      context: session.context || {},
      last_activity_at: new Date().toISOString(),
      message_count: session.messageCount || 0,
    });
    if (error) return { success: false, error: getUserFriendlyError('save session', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save session', error) };
  }
}

// Goal Templates
export async function loadGoalTemplates(): Promise<DataResult<GoalTemplate[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('goal_templates' as any).select('*') as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load templates', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id,
        name: d.name,
        slug: d.slug,
        description: d.description,
        category: d.category,
        goalType: d.goal_type,
        defaultMilestones: d.default_milestones || [],
        defaultTimelineMonths: d.default_timeline_months,
        isSystem: d.is_system,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load templates', error) };
  }
}

// Budget Categories
export async function loadBudgetCategories(userId: string): Promise<DataResult<BudgetCategory[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('budget_categories' as any).select('*').eq('user_id', userId) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load budgets', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, name: d.name, budgetAmount: d.budget_amount,
        alertThresholdPercent: d.alert_threshold_percent, color: d.color, createdAt: d.created_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load budgets', error) };
  }
}

export async function saveBudgetCategory(userId: string, cat: Partial<BudgetCategory> & { id: string; name: string; budgetAmount: number }): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('budget_categories' as any).upsert({
      id: cat.id, user_id: userId, name: cat.name, budget_amount: cat.budgetAmount,
      alert_threshold_percent: cat.alertThresholdPercent || 80, color: cat.color || '#FFD700',
    });
    if (error) return { success: false, error: getUserFriendlyError('save budget', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save budget', error) };
  }
}

// Debt Accounts
export async function loadDebtAccounts(userId: string): Promise<DataResult<DebtAccount[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('debt_accounts' as any).select('*').eq('user_id', userId) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load debts', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, name: d.name, debtType: d.debt_type, balance: d.balance,
        interestRate: d.interest_rate, minimumPayment: d.minimum_payment,
        dueDay: d.due_day, createdAt: d.created_at, updatedAt: d.updated_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load debts', error) };
  }
}

export async function saveDebtAccount(userId: string, debt: Partial<DebtAccount> & { id: string; name: string; balance: number }): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('debt_accounts' as any).upsert({
      id: debt.id, user_id: userId, name: debt.name, debt_type: debt.debtType || 'other',
      balance: debt.balance, interest_rate: debt.interestRate || null,
      minimum_payment: debt.minimumPayment || null, due_day: debt.dueDay || null,
      updated_at: new Date().toISOString(),
    });
    if (error) return { success: false, error: getUserFriendlyError('save debt', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save debt', error) };
  }
}

// Learning Paths
export async function loadLearningPaths(userId: string): Promise<DataResult<LearningPath[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('learning_paths' as any).select('*').eq('user_id', userId).order('created_at', { ascending: false }) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load learning paths', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, name: d.name, description: d.description, pathType: d.path_type as LearningPathType,
        targetRole: d.target_role, targetSkill: d.target_skill, estimatedHours: d.estimated_hours,
        commitmentHoursWeekly: d.commitment_hours_weekly, status: d.status,
        progressPercent: d.progress_percent, startedAt: d.started_at,
        completedAt: d.completed_at, createdAt: d.created_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load learning paths', error) };
  }
}

export async function saveLearningPath(userId: string, path: Partial<LearningPath> & { id: string; name: string }): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('learning_paths' as any).upsert({
      id: path.id, user_id: userId, name: path.name, description: path.description || null,
      path_type: path.pathType || 'custom', target_role: path.targetRole || null,
      target_skill: path.targetSkill || null, estimated_hours: path.estimatedHours || null,
      commitment_hours_weekly: path.commitmentHoursWeekly || null, status: path.status || 'active',
      progress_percent: path.progressPercent || 0,
    });
    if (error) return { success: false, error: getUserFriendlyError('save learning path', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save learning path', error) };
  }
}

// Saved Jobs
export async function loadSavedJobs(userId: string): Promise<DataResult<SavedJob[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('saved_jobs' as any).select('*').eq('user_id', userId).order('created_at', { ascending: false }) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load saved jobs', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, jobPostingId: d.job_posting_id, customTitle: d.custom_title,
        customCompany: d.custom_company, customUrl: d.custom_url, status: d.status,
        matchScore: d.match_score, matchFactors: d.match_factors, notes: d.notes,
        appliedAt: d.applied_at, interviewDate: d.interview_date, offerAmount: d.offer_amount,
        createdAt: d.created_at, updatedAt: d.updated_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load saved jobs', error) };
  }
}

export async function saveSavedJob(userId: string, job: Partial<SavedJob> & { id: string }): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('saved_jobs' as any).upsert({
      id: job.id, user_id: userId, job_posting_id: job.jobPostingId || null,
      custom_title: job.customTitle || null, custom_company: job.customCompany || null,
      custom_url: job.customUrl || null, status: job.status || 'discovered',
      match_score: job.matchScore || null, match_factors: job.matchFactors || {},
      notes: job.notes || null, applied_at: job.appliedAt || null,
      interview_date: job.interviewDate || null, offer_amount: job.offerAmount || null,
      updated_at: new Date().toISOString(),
    });
    if (error) return { success: false, error: getUserFriendlyError('save job', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save job', error) };
  }
}

// Mock Interviews
export async function loadMockInterviews(userId: string): Promise<DataResult<MockInterviewSession[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('mock_interview_sessions' as any).select('*').eq('user_id', userId).order('created_at', { ascending: false }) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load interviews', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, targetRole: d.target_role, targetCompany: d.target_company,
        interviewType: d.interview_type, difficulty: d.difficulty,
        questionsCount: d.questions_count, overallScore: d.overall_score,
        feedbackSummary: d.feedback_summary, strengths: d.strengths,
        improvements: d.improvements, durationMinutes: d.duration_minutes,
        status: d.status, startedAt: d.started_at, completedAt: d.completed_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load interviews', error) };
  }
}

export async function saveMockInterview(userId: string, session: Partial<MockInterviewSession> & { id: string; targetRole: string }): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('mock_interview_sessions' as any).upsert({
      id: session.id, user_id: userId, target_role: session.targetRole,
      target_company: session.targetCompany || null, interview_type: session.interviewType || 'behavioral',
      difficulty: session.difficulty || 'intermediate', questions_count: session.questionsCount || 0,
      overall_score: session.overallScore || null, feedback_summary: session.feedbackSummary || null,
      strengths: session.strengths || [], improvements: session.improvements || [],
      duration_minutes: session.durationMinutes || null, status: session.status || 'in-progress',
      completed_at: session.completedAt || null,
    });
    if (error) return { success: false, error: getUserFriendlyError('save interview', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save interview', error) };
  }
}

// Resumes
export async function loadResumes(userId: string): Promise<DataResult<Resume[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('resumes' as any).select('*').eq('user_id', userId).order('updated_at', { ascending: false }) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load resumes', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, name: d.name, filePath: d.file_path, fileType: d.file_type,
        extractedText: d.extracted_text, analysis: d.analysis,
        optimizationSuggestions: d.optimization_suggestions, targetRole: d.target_role,
        score: d.score, createdAt: d.created_at, updatedAt: d.updated_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load resumes', error) };
  }
}

// Export Jobs
export async function loadExportJobs(userId: string): Promise<DataResult<ExportJob[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('export_jobs' as any).select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(20) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load exports', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, format: d.format, categories: d.categories, status: d.status,
        downloadUrl: d.download_url, fileSize: d.file_size, expiresAt: d.expires_at,
        createdAt: d.created_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load exports', error) };
  }
}

export async function createExportJob(userId: string, format: ExportFormat, categories: string[]): Promise<DataResult<{ id: string }>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('export_jobs' as any).insert({
      user_id: userId, format, categories, status: 'queued',
    }).select('id').single() as { data: any; error: any };
    if (error) return { success: false, error: getUserFriendlyError('create export', error) };
    return { success: true, data: { id: data.id } };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('create export', error) };
  }
}

// Webhook Subscriptions
export async function loadWebhookSubscriptions(userId: string): Promise<DataResult<WebhookSubscription[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('webhook_subscriptions' as any).select('*').eq('user_id', userId) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load webhooks', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, url: d.url, events: d.events, active: d.active,
        description: d.description, createdAt: d.created_at,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load webhooks', error) };
  }
}

// Agent Configuration
export async function loadAgentConfigurations(userId: string): Promise<DataResult<AgentConfiguration[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('agent_configurations' as any).select('*').eq('user_id', userId) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load agent configs', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        id: d.id, agentType: d.agent_type, reviewFrequency: d.review_frequency as TrackingFrequency,
        autoApplyThreshold: d.auto_apply_threshold, sensitivity: d.sensitivity,
        customRules: d.custom_rules, enabled: d.enabled,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load agent configs', error) };
  }
}

export async function saveAgentConfiguration(userId: string, config: Partial<AgentConfiguration> & { id: string; agentType: string }): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('agent_configurations' as any).upsert({
      id: config.id, user_id: userId, agent_type: config.agentType,
      review_frequency: config.reviewFrequency || 'weekly',
      auto_apply_threshold: config.autoApplyThreshold ?? 0.9,
      sensitivity: config.sensitivity ?? 0, custom_rules: config.customRules || {},
      enabled: config.enabled ?? true, updated_at: new Date().toISOString(),
    });
    if (error) return { success: false, error: getUserFriendlyError('save agent config', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save agent config', error) };
  }
}

// Notification Preferences
export async function loadNotificationPreferences(userId: string): Promise<DataResult<NotificationPreference[]>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await supabase.from('notification_preferences' as any).select('*').eq('user_id', userId) as { data: any[] | null; error: any };
    if (error) return { success: false, error: getUserFriendlyError('load notification prefs', error) };
    return {
      success: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: (data || []).map((d: any) => ({
        category: d.category, enabled: d.enabled, delivery: d.delivery, frequency: d.frequency,
      })),
    };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('load notification prefs', error) };
  }
}

export async function saveNotificationPreference(userId: string, pref: NotificationPreference): Promise<DataResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from('notification_preferences' as any).upsert({
      user_id: userId, category: pref.category, enabled: pref.enabled,
      delivery: pref.delivery, frequency: pref.frequency,
    });
    if (error) return { success: false, error: getUserFriendlyError('save notification pref', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save notification pref', error) };
  }
}

// Career Momentum Score
export async function saveCareerMomentumScore(userId: string, score: number): Promise<DataResult> {
  try {
    const { error } = await supabase.from('profiles').update({
      career_momentum_score: score,
      career_momentum_updated_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }).eq('id', userId);
    if (error) return { success: false, error: getUserFriendlyError('save momentum score', error) };
    return { success: true };
  } catch (error) {
    return { success: false, error: getUserFriendlyError('save momentum score', error) };
  }
}
